import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { blogAPI } from '../utils/api';
import Button from '../components/Button';
import ReactMarkdown from 'react-markdown';

interface BlogPost {
  _id: string;
  title: string;
  content: string;
  author: string | { _id: string; username: string };
  categories: string[];
  slug: string;
  featuredImage: string;
  publishDate: string;
  published?: boolean;
}

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const response = await blogAPI.getPostBySlug(slug as string);
        setPost(response.data);
        
        // Fetch related posts in the same category
        if (response.data.categories && response.data.categories.length > 0) {
          const relatedResponse = await blogAPI.getPosts(1, 3, response.data.categories[0], undefined, undefined, true);
          
          // Filter out the current post from related posts
          const filteredRelated = relatedResponse.data.blogPosts.filter(
            (relatedPost: BlogPost) => relatedPost.slug !== slug
          );
          
          setRelatedPosts(filteredRelated);
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching blog post:', err);
        setError('Der opstod en fejl ved indlæsning af blogindlægget. Prøv igen senere.');
        setLoading(false);
      }
    };

    if (slug) {
      fetchPost();
    }
  }, [slug]);

  if (loading) {
    return (
      <div className="container-custom py-12 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-snudefryd-blue"></div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="container-custom py-12">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          <p>{error || 'Blogindlægget kunne ikke findes.'}</p>
        </div>
        <Link to="/blog">
          <Button variant="secondary">Tilbage til blog</Button>
        </Link>
      </div>
    );
  }

  // Format date
  const formattedDate = new Date(post.publishDate).toLocaleDateString('da-DK', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  // Get author name, handling both string and object formats
  const getAuthorName = (author: string | { _id: string; username: string }): string => {
    if (typeof author === 'string') {
      return author;
    }
    return author.username || 'Ukendt forfatter';
  };

  const authorName = getAuthorName(post.author);

  return (
    <div>
      {/* Hero Section with Image */}
      <div className="w-full h-64 md:h-96 relative">
        <img 
          src={post.featuredImage || 'https://cdn.pixabay.com/photo/2017/07/31/21/15/dog-2561134_1280.jpg'} 
          alt={post.title} 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-40 flex items-end">
          <div className="container-custom pb-8 text-white">
            <span className="inline-block bg-snudefryd-pink px-3 py-1 text-sm font-semibold rounded mb-3">
              {post.categories.join(', ')}
            </span>
            <h1 className="text-3xl md:text-4xl font-bold mb-2">{post.title}</h1>
            <div className="flex items-center text-sm">
              <span>Af {authorName}</span>
              <span className="mx-2">•</span>
              <span>{formattedDate}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container-custom py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Article Content */}
          <div className="lg:col-span-2">
            <article className="prose prose-lg max-w-none">
              {/* Render content with ReactMarkdown */}
              <ReactMarkdown>{post.content}</ReactMarkdown>
            </article>

            {/* Share Buttons */}
            <div className="mt-12 border-t border-gray-200 pt-6">
              <h3 className="text-lg font-semibold mb-4">Del dette indlæg</h3>
              <div className="flex space-x-4">
                <button className="p-2 bg-blue-600 text-white rounded-full">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723 10.054 10.054 0 01-3.127 1.184 4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                  </svg>
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Author Info */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
              <h3 className="text-lg font-semibold mb-4">Om forfatteren</h3>
              <div className="flex items-center mb-4">
                <img 
                  src="/Erica.png" // Replace with the actual image URL
                  alt={authorName} // Use the author's name as alt text
                  className="w-12 h-12 rounded-full mr-4 object-cover" // Ensure the image is rounded
                />
                <div>
                  <p className="font-medium">{authorName}</p>
                  <p className="text-sm text-gray-600">Rådgiver</p>
                </div>
              </div>
              <p className="text-sm text-gray-700">
                Erica er en passioneret hundeernæringsrådgiver. Hun deler regelmæssigt sin viden gennem blogindlæg og konsultationer.
              </p>
            </div>

            {/* Related Posts */}
            {relatedPosts.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold mb-4">Relaterede indlæg</h3>
                <div className="space-y-4">
                  {relatedPosts.map((relatedPost) => (
                    <Link 
                      key={relatedPost._id} 
                      to={`/blog/${relatedPost.slug}`}
                      className="block group"
                    >
                      <div className="flex items-start">
                        <div className="w-16 h-16 rounded overflow-hidden mr-4 flex-shrink-0">
                          <img 
                            src={relatedPost.featuredImage || 'https://images.unsplash.com/photo-1583337130417-3346a1be7dee?ixlib=rb-1.2.1&auto=format&fit=crop&w=200&q=80'} 
                            alt={relatedPost.title} 
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900 group-hover:text-snudefryd-blue transition-colors">
                            {relatedPost.title}
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(relatedPost.publishDate).toLocaleDateString('da-DK')}
                          </p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
                <div className="mt-6">
                  <Link to="/blog">
                    <Button variant="secondary" fullWidth>Se alle indlæg</Button>
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPostPage; 
